/**
 * Product page sticky add-to-cart bar.
 *
 * Powers <product-sticky-bar>. Deliberately owns no cart logic: the button clicks the
 * real add-to-cart button, so the add goes through whatever path the theme already
 * uses (product-form's AJAX submit, or a native POST when cart_type is 'page').
 * Loading/disabled state is mirrored off that same button.
 *
 * Visibility: shown once the main buy button has scrolled ABOVE the viewport, hidden
 * again when the footer comes into view so the bar never covers it.
 *
 * Quantity and price stay in sync with the product form's quantity input, so the pack
 * selector, the stepper and this bar can never disagree.
 *
 * Everything is wrapped so a failure here can never block add-to-cart: on any error
 * the bar simply stays hidden and the normal buy button is untouched.
 */
(function () {
  function formatMoney(cents) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var fmt = (window.theme && window.theme.money_format) || '';
        return window.Shopify.formatMoney(Math.round(cents), fmt);
      }
    } catch (e) {}
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  class ProductStickyBar extends HTMLElement {
    connectedCallback() {
      try {
        this.formId = this.getAttribute('form-id') || '';
        this.unitPrice = parseInt(this.getAttribute('unit-price'), 10) || 0;
        this.secondPercent = parseFloat(this.getAttribute('second-percent'));
        if (isNaN(this.secondPercent)) this.secondPercent = 0; // 0 = no BOGO maths

        // Scope tightly: complementary products and quick-shop render their own
        // .js-product-add buttons, so a document-wide query could grab the wrong one.
        // The form is authoritative — the buy button is its 'add' element, exactly
        // what product-form.js binds to.
        this.scope = document.querySelector('.product-single__box') || document;
        this.form = this.formId ? document.getElementById(this.formId) : null;
        this.realBtn =
          (this.form && this.form.elements && this.form.elements['add']) ||
          this.scope.querySelector('.js-product-add');
        this.qtyInput = document.getElementById(this.formId + '--quantity');

        this.addBtn = this.querySelector('[data-sticky-add]');
        this.qtyOut = this.querySelector('[data-sticky-qty]');
        this.priceOut = this.querySelector('[data-sticky-price]');
        this.wasOut = this.querySelector('[data-sticky-was]');
        this.thumb = this.querySelector('[data-sticky-thumb]');
        this.variantOut = this.querySelector('[data-sticky-variant]');

        if (!this.realBtn || !this.addBtn) return; // nothing to proxy: stay hidden

        this._bindAdd();
        this._bindQty();
        this._bindVariantChange();
        this._mirrorButtonState();
        this._observeVisibility();
        this._render();
      } catch (e) {}
    }

    disconnectedCallback() {
      try {
        if (this._btnObserver) this._btnObserver.disconnect();
        if (this._showObserver) this._showObserver.disconnect();
        if (this._footObserver) this._footObserver.disconnect();
        if (this.qtyInput && this._onQtyChange) {
          this.qtyInput.removeEventListener('change', this._onQtyChange);
          this.qtyInput.removeEventListener('input', this._onQtyChange);
        }
        if (this._variantEl && this._onVariantChange) {
          this._variantEl.removeEventListener('variants:change', this._onVariantChange);
        }
        this._setBodyFlag(false);
      } catch (e) {}
    }

    /* ---- add to cart: proxy the real button ---- */
    _bindAdd() {
      this.addBtn.addEventListener('click', (e) => {
        try {
          e.preventDefault();
          // Re-entrancy guard: the cart round trip takes a moment, and a shopper who
          // sees nothing happen taps again — which would fire a second add.
          if (this.realBtn.disabled || this._adding) return;
          // Paint the busy state on THIS button synchronously rather than waiting for
          // the MutationObserver to mirror the real one. Silence reads as a dead tap.
          this._adding = true;
          this.addBtn.classList.add('is-adding');
          this.realBtn.click();
        } catch (err) {}
      });
    }

    // product-form toggles is-adding/is-added on the real button; mirror it so the
    // shopper gets feedback on the button they actually pressed.
    _mirrorButtonState() {
      try {
        var sync = () => {
          try {
            var busy = this.realBtn.classList.contains('is-adding');
            // Only release our own optimistic flag once we've actually observed the
            // real button go busy and then come back — otherwise a sync firing between
            // the tap and product-form's class change would clear it immediately.
            if (busy) this._sawBusy = true;
            if (!busy && this._sawBusy) {
              this._adding = false;
              this._sawBusy = false;
            }
            this.addBtn.classList.toggle('is-adding', busy || !!this._adding);
            this.addBtn.classList.toggle('is-added', this.realBtn.classList.contains('is-added'));
            this.addBtn.disabled = !!this.realBtn.disabled;
          } catch (e) {}
        };
        sync();
        this._btnObserver = new MutationObserver(sync);
        this._btnObserver.observe(this.realBtn, { attributes: true, attributeFilter: ['class', 'disabled'] });
      } catch (e) {}
    }

    /* ---- quantity: two-way with the form's input ---- */
    _bindQty() {
      try {
        var minus = this.querySelector('[data-sticky-minus]');
        var plus = this.querySelector('[data-sticky-plus]');
        if (minus) minus.addEventListener('click', () => this._step(-1));
        if (plus) plus.addEventListener('click', () => this._step(1));

        if (this.qtyInput) {
          this._onQtyChange = () => this._render();
          this.qtyInput.addEventListener('change', this._onQtyChange);
          this.qtyInput.addEventListener('input', this._onQtyChange);
        }
      } catch (e) {}
    }

    _qty() {
      try {
        var n = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
        return !n || n < 1 ? 1 : n;
      } catch (e) {
        return 1;
      }
    }

    _step(delta) {
      try {
        var next = this._qty() + delta;
        if (next < 1) next = 1;
        if (!this.qtyInput) return;
        this.qtyInput.value = next;
        this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        this._render();
      } catch (e) {}
    }

    /* ---- variant changes ---- */
    _bindVariantChange() {
      try {
        this._variantEl = this.scope.querySelector(
          'variant-selects, variant-radios, variant-swatches, variant-mixed-inputs'
        );
        if (!this._variantEl) return;
        this._onVariantChange = (e) => {
          try {
            var v = e && e.detail && e.detail.variant;
            if (!v) return;
            if (typeof v.price === 'number') this.unitPrice = v.price;
            if (this.variantOut && v.title) this.variantOut.textContent = v.title;
            if (this.thumb && v.featured_image && v.featured_image.src) {
              this.thumb.src = v.featured_image.src;
              this.thumb.removeAttribute('srcset');
            }
            // The real button's label changes on variant switch too; mirror it so the
            // bar can't offer "Add to cart" for a sold-out variant.
            this._setLabel(v.available);
            this._render();
          } catch (err) {}
        };
        this._variantEl.addEventListener('variants:change', this._onVariantChange);
      } catch (e) {}
    }

    _setLabel(available) {
      try {
        var t = (window.theme && window.theme.t) || {};
        var text = available ? t.add_to_cart : t.sold_out;
        // Write into the label span only. Setting textContent on the button itself would
        // destroy the tick and spinner spans the theme's state CSS depends on.
        var labelEl = this.addBtn.querySelector('.js-product-add-text') || this.addBtn;
        if (text) labelEl.textContent = text;
        this.addBtn.disabled = !available;
      } catch (e) {}
    }

    /* ---- price for the current quantity, BOGO applied when configured ---- */
    _render() {
      try {
        var qty = this._qty();
        if (this.qtyOut) this.qtyOut.textContent = qty;

        var full = this.unitPrice * qty;
        var total = full;
        if (this.secondPercent > 0) {
          // One discounted item per pair, matching a Buy X Get Y automatic discount.
          var pairs = Math.floor(qty / 2);
          total = full - pairs * Math.round((this.unitPrice * this.secondPercent) / 100);
        }

        if (this.priceOut) this.priceOut.textContent = formatMoney(total);
        if (this.wasOut) {
          if (total < full) {
            this.wasOut.textContent = formatMoney(full);
            this.wasOut.hidden = false;
          } else {
            this.wasOut.hidden = true;
          }
        }
      } catch (e) {}
    }

    /* ---- visibility ---- */
    _observeVisibility() {
      try {
        var anchor =
          (this.form && this.form.querySelector('.js-product-buttons')) ||
          this.scope.querySelector('.js-product-buttons') ||
          this.realBtn;
        if (!anchor || !('IntersectionObserver' in window)) return;

        this._pastButton = false;
        this._atFooter = false;

        this._showObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Only once the button has scrolled off the TOP — never before the
              // shopper has reached it on the way down.
              this._pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            });
            this._apply();
          },
          { threshold: 0 }
        );
        this._showObserver.observe(anchor);

        var footer = document.querySelector('.footer');
        if (footer) {
          this._footObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this._atFooter = entry.isIntersecting;
              });
              this._apply();
            },
            { threshold: 0 }
          );
          this._footObserver.observe(footer);
        }
      } catch (e) {}
    }

    _apply() {
      try {
        var show = this._pastButton && !this._atFooter;
        this.classList.toggle('is-visible', show);
        this._setBodyFlag(show);
      } catch (e) {}
    }

    // Lets CSS lift the back-to-top button clear of the bar.
    _setBodyFlag(on) {
      try {
        document.body.classList.toggle('has-pdp-sticky', !!on);
      } catch (e) {}
    }
  }

  try {
    if (window.customElements && !customElements.get('product-sticky-bar')) {
      customElements.define('product-sticky-bar', ProductStickyBar);
    }
  } catch (e) {}
})();
